import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { AuthenticatorAssuranceLevels, User } from '@supabase/supabase-js';
import type { AppData, UserProfile, BudgetLimit, Expense, CalendarEvent, Chore, LifeEvent } from '@/types';
import { getNextRecurringExpenseDate } from '@/lib/money-tools';
import { readScopedLocalJson, removeScopedLocalJson, writeScopedLocalJson } from '@/lib/offline-core';
import { buildHouseholdShareScopeFilter, mapHouseholdShareRow } from '@/lib/household-share';
import {
  buildExpenseReceiptStoragePath,
  DEFAULT_BUDGET_LIMITS,
  normaliseBudgetCategoryLabel,
  type BudgetCategoryPreference,
} from '../../ui-foundation/features/money';
import { calculateAgeFromDateOfBirth, resolveStoredAge } from '../../ui-foundation/features/family';
import type { OfflineCoreQueueItem } from '../../ui-foundation/core';
import { reportRuntimeEvent } from '@/lib/runtime-monitor';
import { isTransientConnectionError, useNetworkStatus } from '../../ui-foundation/core';

export type AppRole = 'admin' | 'moderator' | 'user';

const DEFAULT_DATA: AppData = {
    profile: {
      firstName: '',
      avatarUrl: null,
      postcode: '',
    annualIncome: 0,
    employmentType: '',
    partnerName: '',
    partnerDateOfBirth: '',
    children: [],
    workType: '',
    dependantTypes: [],
    dateOfBirth: '',
    screeningInvitationPath: '',
    hasCervix: null,
      breastScreeningRelevant: null,
      prostateScreeningRelevant: null,
      onHrt: false,
      communityAccessStatus: 'active',
    },
  budgetLimits: DEFAULT_BUDGET_LIMITS.map((item) => ({ ...item })),
  budgetCategoryPreferences: [],
  expenses: [],
  householdShare: null,
  events: [],
  chores: [],
  lifeEvents: [],
  benefitsViewed: [],
  childMaintenanceAmount: 0,
  childMaintenanceSchedule: 'monthly',
  documentRenewals: [],
  communityDirectoryEntries: [],
  communityPolicyUpdates: [],
};

const APP_DATA_CACHE_SCOPE = 'app-data';
const CORE_QUEUE_SCOPE = 'core-queue';

type AppOfflineCacheRecord = {
  updatedAt: string;
  data: AppData;
};

function readAppOfflineCache(userId: string) {
  return readScopedLocalJson<AppOfflineCacheRecord>(APP_DATA_CACHE_SCOPE, userId);
}

function writeAppOfflineCache(userId: string, data: AppData) {
  writeScopedLocalJson(APP_DATA_CACHE_SCOPE, userId, {
    updatedAt: new Date().toISOString(),
    data,
  } satisfies AppOfflineCacheRecord);
}

function clearAppOfflineCache(userId: string) {
  removeScopedLocalJson(APP_DATA_CACHE_SCOPE, userId);
}

function readQueuedCoreWrites(userId: string) {
  return readScopedLocalJson<OfflineCoreQueueItem[]>(CORE_QUEUE_SCOPE, userId) || [];
}

function writeQueuedCoreWrites(userId: string, queue: OfflineCoreQueueItem[]) {
  if (!queue.length) {
    removeScopedLocalJson(CORE_QUEUE_SCOPE, userId);
    return;
  }

  writeScopedLocalJson(CORE_QUEUE_SCOPE, userId, queue);
}

async function flushQueuedCoreWrites(userId: string) {
  const queue = readQueuedCoreWrites(userId);
  if (!queue.length) {
    return false;
  }

  const remaining = [...queue];
  const tempIdMap = new Map<string, string>();
  let flushedAny = false;

  while (remaining.length) {
    const item = remaining[0];

    try {
      if (item.kind === 'add-expense') {
        const { error } = await supabase.from('expenses').insert({
          user_id: userId,
          amount: item.expense.amount,
          category: item.expense.category,
          date: item.expense.date,
          description: item.expense.description,
          expense_scope: 'personal',
          household_share_id:
            item.expense.visibility === 'household' ? item.expense.householdShareId || null : null,
          is_recurring: Boolean(item.expense.isRecurring),
          recurring_period: item.expense.isRecurring ? item.expense.recurringPeriod || 'monthly' : 'none',
          is_subscription: Boolean(item.expense.isSubscription && item.expense.isRecurring),
          cancel_intent: Boolean(item.expense.cancelIntent && item.expense.isSubscription && item.expense.isRecurring),
          next_due_date: item.expense.isRecurring ? item.expense.nextDueDate || item.expense.date : null,
          reminder_days_before: item.expense.isRecurring ? item.expense.reminderDaysBefore ?? null : null,
          last_paid_at: item.expense.lastPaidAt || null,
          last_paid_amount: item.expense.lastPaidAmount ?? null,
          notes: item.expense.notes?.trim() || null,
        } as any);
        if (error) throw error;
      } else if (item.kind === 'add-chore') {
        const { data: inserted, error } = await supabase
          .from('chores')
          .insert({
            user_id: userId,
            task: item.chore.task,
            assigned_to: item.chore.assignedTo,
            done: item.chore.done,
            week_of: item.chore.weekOf,
            household_share_id: item.chore.householdShareId || null,
          })
          .select('id')
          .single();

        if (error) throw error;
        if (inserted?.id) {
          tempIdMap.set(item.chore.id, inserted.id);
        }
      } else if (item.kind === 'toggle-chore') {
        const resolvedId = tempIdMap.get(item.choreId) || item.choreId;
        const { error } = await supabase.from('chores').update({ done: item.done }).eq('id', resolvedId);
        if (error) throw error;
      }

      remaining.shift();
      flushedAny = true;
    } catch (error) {
      if (isTransientConnectionError(error)) {
        break;
      }
      throw error;
    }
  }

  writeQueuedCoreWrites(userId, remaining);
  return flushedAny;
}

function notifyDataChanged() {
  window.dispatchEvent(new CustomEvent('adulting:data-changed'));
}

function expenseFingerprint(expense: Pick<Expense, 'date' | 'amount' | 'category' | 'description' | 'visibility'>) {
  return [
    expense.date,
    expense.amount.toFixed(2),
    expense.category,
    expense.description.trim().toLowerCase(),
    expense.visibility || 'personal',
  ].join('::');
}

type CommunityPolicyLookupResult = {
  postcode: string;
  authorityName: string;
  councilUrl: string;
  authorityTier: string | null;
  authoritySlug: string | null;
  councilSearchUrl: string;
  generatedAt: string;
};

function normalizeSavedPostcode(value?: string | null) {
  return (value || '').trim().toUpperCase().replace(/\s+/g, ' ');
}

function buildCommunityPolicyProfileTags(profile: UserProfile) {
  const tags = new Set<string>(['general', 'health']);

  if (profile.children.some((child) => child.name.trim().length > 0)) tags.add('family');
  if (profile.dependantTypes.includes('elderly') || profile.dependantTypes.includes('disabled')) tags.add('caring');
  if (profile.hasCervix || profile.onHrt || profile.screeningInvitationPath === 'female') tags.add('womens-health');
  if (profile.workType === 'self-employed' || profile.employmentType === 'self-employed') tags.add('self-employed');

  const annualIncome = Number(profile.annualIncome || 0);
  if (annualIncome > 0 && annualIncome <= 35000) tags.add('money-pressure');

  return [...tags];
}

function buildGeneratedCommunityPolicyUpdates({
  lookup,
  profileTags,
}: {
  lookup: CommunityPolicyLookupResult | null;
  profileTags: string[];
}) {
  if (!lookup) return [] as AppData['communityPolicyUpdates'];

  const entries: AppData['communityPolicyUpdates'] = [
    {
      id: `local-scheme-${lookup.authoritySlug || lookup.postcode}-general`,
      title: `${lookup.authorityName}: local support schemes worth checking`,
      summary: `Adulting matched your saved postcode to ${lookup.authorityName}, so this keeps the council route nearby for local welfare, food, energy, and essentials help.`,
      sourceName: lookup.authorityName,
      sourceUrl: lookup.councilUrl,
      reviewedAt: lookup.generatedAt,
      targetTags: ['general', 'health', 'money-pressure', 'family', 'caring'],
      origin: 'local-scheme',
      localityLabel: lookup.authorityName,
      reasonLabel: `Matched from saved postcode ${lookup.postcode}.`,
      linkLabel: `Open ${lookup.authorityName}`,
    },
  ];

  if (profileTags.includes('caring')) {
    entries.push({
      id: `local-scheme-${lookup.authoritySlug || lookup.postcode}-caring`,
      title: `${lookup.authorityName}: local carer support and assessment routes`,
      summary: 'Council-side carer support, assessments, and respite can move separately from national benefits, so Adulting keeps the local route visible too.',
      sourceName: lookup.authorityName,
      sourceUrl: lookup.councilUrl,
      reviewedAt: lookup.generatedAt,
      targetTags: ['caring'],
      origin: 'local-scheme',
      localityLabel: lookup.authorityName,
      reasonLabel: 'Shown because your profile includes caring responsibilities.',
      linkLabel: `Open ${lookup.authorityName}`,
    });
  }

  if (profileTags.includes('family')) {
    entries.push({
      id: `local-scheme-${lookup.authoritySlug || lookup.postcode}-family`,
      title: `${lookup.authorityName}: local family and holiday-support routes`,
      summary: 'Council-side family support, school-holiday help, childcare, or SEND routes can change by area and time of year, so Adulting keeps the local route nearby.',
      sourceName: lookup.authorityName,
      sourceUrl: lookup.councilUrl,
      reviewedAt: lookup.generatedAt,
      targetTags: ['family'],
      origin: 'local-scheme',
      localityLabel: lookup.authorityName,
      reasonLabel: 'Shown because your profile includes children or school admin.',
      linkLabel: `Open ${lookup.authorityName}`,
    });
  }

  return entries;
}

function mapExpenseRow(row: {
  id: string;
  amount: number;
  category: string;
  date: string;
  description: string | null;
  is_recurring?: boolean | null;
  recurring_period?: string | null;
  is_subscription?: boolean | null;
  cancel_intent?: boolean | null;
  next_due_date?: string | null;
  reminder_days_before?: number | null;
  last_paid_at?: string | null;
  last_paid_amount?: number | null;
  notes?: string | null;
  receipt_file_name?: string | null;
  receipt_file_type?: string | null;
  receipt_file_size?: number | null;
  receipt_storage_path?: string | null;
  household_share_id?: string | null;
  user_id?: string;
}) {
  return {
    id: row.id,
    amount: Number(row.amount || 0),
    category: row.category as Expense['category'],
    date: row.date,
    description: row.description || '',
    userId: row.user_id,
    visibility: row.household_share_id ? 'household' : 'personal',
    householdShareId: row.household_share_id || null,
    isRecurring: Boolean(row.is_recurring),
    recurringPeriod:
      row.recurring_period === 'monthly' || row.recurring_period === 'yearly'
        ? row.recurring_period
        : 'none',
    isSubscription: Boolean(row.is_subscription),
    cancelIntent: Boolean(row.cancel_intent),
    nextDueDate: row.next_due_date || undefined,
    reminderDaysBefore: row.reminder_days_before ?? undefined,
    lastPaidAt: row.last_paid_at || undefined,
    lastPaidAmount: row.last_paid_amount == null ? undefined : Number(row.last_paid_amount),
    notes: row.notes || undefined,
    receiptFileName: row.receipt_file_name || undefined,
    receiptFileType: row.receipt_file_type || undefined,
    receiptFileSize: row.receipt_file_size == null ? undefined : Number(row.receipt_file_size),
    receiptStoragePath: row.receipt_storage_path || undefined,
  } satisfies Expense;
}

interface AppContextType {
  data: AppData;
  user: User | null;
  loading: boolean;
  securityLoading: boolean;
  role: AppRole;
  isPrivilegedUser: boolean;
  mfaLevel: AuthenticatorAssuranceLevels | null;
  hasVerifiedMfaFactor: boolean;
  requiresMfa: boolean;
  requiresPrivilegedMfaSetup: boolean;
  refreshSecurity: () => Promise<void>;
  refreshData: () => Promise<void>;
  updateProfile: (profile: UserProfile) => Promise<void>;
  updateProfileAvatar: (avatarUrl: string | null) => Promise<void>;
  updateChildMaintenance: (amount: number, schedule: string) => Promise<void>;
  updateBudgetLimits: (limits: BudgetLimit[]) => Promise<void>;
  addBudgetCategory: (label: string) => Promise<boolean>;
  renameBudgetCategory: (currentLabel: string, nextLabel: string) => Promise<boolean>;
  setBudgetCategoryHidden: (label: string, hidden: boolean) => Promise<boolean>;
  addExpense: (expense: Expense, receiptFile?: File | null) => Promise<boolean>;
  updateRecurringExpenseFlags: (
    expenseId: string,
    updates: { isSubscription?: boolean; cancelIntent?: boolean },
  ) => Promise<boolean>;
  recordRecurringExpensePayment: (expenseId: string, amount: number) => Promise<void>;
  bulkAddExpenses: (expenses: Expense[]) => Promise<number>;
  deleteExpense: (id: string) => void;
  addEvent: (event: CalendarEvent) => Promise<CalendarEvent | null>;
  deleteEvent: (id: string) => void;
  addChore: (chore: Chore) => void;
  toggleChore: (id: string) => void;
  addLifeEvent: (event: LifeEvent) => void;
  markBenefitViewed: (name: string) => void;
  signOut: () => void;
}

const AppContext = createContext<AppContextType | null>(null);

function getHighestRole(roles: AppRole[]): AppRole {
  if (roles.includes('admin')) return 'admin';
  if (roles.includes('moderator')) return 'moderator';
  return 'user';
}

function toRuntimeMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
    return (error as { message: string }).message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return 'Unknown error';
  }
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const { isOffline, markConnectionHealthy, reportConnectionIssue } = useNetworkStatus();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [securityLoading, setSecurityLoading] = useState(true);
  const [role, setRole] = useState<AppRole>('user');
  const [mfaLevel, setMfaLevel] = useState<AuthenticatorAssuranceLevels | null>(null);
  const [hasVerifiedMfaFactor, setHasVerifiedMfaFactor] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [requiresPrivilegedMfaSetup, setRequiresPrivilegedMfaSetup] = useState(false);
  const [data, setData] = useState<AppData>(DEFAULT_DATA);

  // Auth listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        setData(DEFAULT_DATA);
        setLoading(false);
        setSecurityLoading(false);
        setRole('user');
        setRequiresMfa(false);
        setHasVerifiedMfaFactor(false);
        setRequiresPrivilegedMfaSetup(false);
        setMfaLevel(null);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        setLoading(false);
        setSecurityLoading(false);
        setRole('user');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const refreshSecurity = useCallback(async () => {
    if (!user) {
      setRole('user');
      setRequiresMfa(false);
      setHasVerifiedMfaFactor(false);
      setRequiresPrivilegedMfaSetup(false);
      setMfaLevel(null);
      setSecurityLoading(false);
      return;
    }

    setSecurityLoading(true);
    try {
      const [assuranceResult, factorResult, roleResult] = await Promise.allSettled([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
        supabase.from('user_roles').select('role').eq('user_id', user.id),
      ]);

      const assurance =
        assuranceResult.status === 'fulfilled' ? assuranceResult.value.data : null;
      const assuranceError =
        assuranceResult.status === 'fulfilled' ? assuranceResult.value.error : assuranceResult.reason;

      const factorData =
        factorResult.status === 'fulfilled' ? factorResult.value.data : null;
      const factorError =
        factorResult.status === 'fulfilled' ? factorResult.value.error : factorResult.reason;

      const roleRows =
        roleResult.status === 'fulfilled' ? roleResult.value.data : null;
      const roleError =
        roleResult.status === 'fulfilled' ? roleResult.value.error : roleResult.reason;

      const highestRole = roleError
        ? 'user'
        : getHighestRole(((roleRows || []) as { role: AppRole }[]).map((entry) => entry.role));
      const verifiedFactorCount = factorData?.totp.filter((factor) => factor.status === 'verified').length ?? 0;

      if (roleError && !isTransientConnectionError(roleError)) {
        console.error('Error fetching user role:', roleError);
        void reportRuntimeEvent({
          userId: user.id,
          source: 'app-security.role',
          message: toRuntimeMessage(roleError),
          path: window.location.pathname,
        });
      }

      if (factorError && !isTransientConnectionError(factorError)) {
        console.error('Error fetching MFA factors:', factorError);
        void reportRuntimeEvent({
          userId: user.id,
          source: 'app-security.mfa-factors',
          message: toRuntimeMessage(factorError),
          path: window.location.pathname,
        });
      }

      setRole(highestRole);
      setHasVerifiedMfaFactor(verifiedFactorCount > 0);
      setRequiresPrivilegedMfaSetup(
        (highestRole === 'admin' || highestRole === 'moderator') && verifiedFactorCount === 0,
      );

      if (assuranceError) {
        if (!isTransientConnectionError(assuranceError)) {
          console.error('Error fetching MFA assurance level:', assuranceError);
          void reportRuntimeEvent({
            userId: user.id,
            source: 'app-security.mfa-assurance',
            message: toRuntimeMessage(assuranceError),
            path: window.location.pathname,
          });
        }
        setRequiresMfa(false);
        setMfaLevel(null);
      } else {
        setMfaLevel(assurance.currentLevel);
        setRequiresMfa(assurance.nextLevel === 'aal2' && assurance.currentLevel !== 'aal2');
      }

      if (
        isTransientConnectionError(roleError) ||
        isTransientConnectionError(factorError) ||
        isTransientConnectionError(assuranceError)
      ) {
        reportConnectionIssue(undefined, 'Adulting could not finish checking your account because the connection dropped.');
      } else {
        markConnectionHealthy();
      }
    } finally {
      setSecurityLoading(false);
    }
  }, [markConnectionHealthy, reportConnectionIssue, user]);

  // Fetch all data when user logs in
  useEffect(() => {
    if (!user) return;
    const cached = readAppOfflineCache(user.id);
    if (cached?.data) {
      setData(cached.data);
      setLoading(false);
    }
    fetchAllData(user.id);
  }, [user]);

  useEffect(() => {
    void refreshSecurity();
  }, [refreshSecurity]);

  useEffect(() => {
    if (!user || typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => {
      void fetchAllData(user.id);
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user]);

async function fetchAllData(userId: string) {
    try {
      try {
        await flushQueuedCoreWrites(userId);
      } catch (queueError) {
        if (!isTransientConnectionError(queueError)) {
          throw queueError;
        }
      }

      const anyClient = supabase as any;
      const householdShareResult = await anyClient
        .from('household_shares')
        .select('*')
        .or(`owner_user_id.eq.${userId},member_user_id.eq.${userId}`)
        .maybeSingle();
      if (householdShareResult.error) {
        throw householdShareResult.error;
      }
      const householdShare = mapHouseholdShareRow(householdShareResult.data);
      const expenseScope = buildHouseholdShareScopeFilter(userId, householdShare?.id || null);
      const choreScope = buildHouseholdShareScopeFilter(userId, householdShare?.id || null);

      const [profileRes, childrenRes, budgetRes, budgetCategoryRes, expensesRes, eventsRes, choresRes, lifeEventsRes, benefitsRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('user_id', userId).single(),
        supabase.from('children').select('*').eq('user_id', userId),
        supabase.from('budget_limits').select('*').eq('user_id', userId),
        supabase.from('budget_category_preferences' as any).select('*').eq('user_id', userId).order('updated_at', { ascending: false }),
        anyClient.from('expenses').select('*').eq('expense_scope', 'personal').or(expenseScope),
        supabase.from('events').select('*').eq('user_id', userId),
        anyClient.from('chores').select('*').or(choreScope),
        supabase.from('life_events').select('*').eq('user_id', userId),
        supabase.from('benefits_viewed').select('*').eq('user_id', userId),
      ]);

      const profile = profileRes.data;
      const childrenData = childrenRes.data || [];

      const nextData: AppData = {
        profile: {
          firstName: profile?.first_name || '',
          avatarUrl: (profile as any)?.avatar_url || null,
          postcode: profile?.postcode || '',
          annualIncome: profile?.annual_income || 0,
          employmentType: (profile?.employment_type || '') as UserProfile['employmentType'],
          partnerName: (profile as any)?.partner_name || '',
          partnerDateOfBirth: ((profile as any)?.partner_date_of_birth || '') as string,
          children: childrenData.map(c => ({
            id: c.id,
            name: c.name,
            age: resolveStoredAge(c.date_of_birth || null, c.age) || 0,
            dateOfBirth: c.date_of_birth || null,
            schoolName: c.school_name || null,
            schoolUrn: c.school_urn || null,
          })),
          workType: ((profile as any)?.work_type || '') as UserProfile['workType'],
          dependantTypes: ((profile as any)?.dependant_types || []) as UserProfile['dependantTypes'],
          dateOfBirth: ((profile as any)?.date_of_birth || '') as string,
          screeningInvitationPath: ((profile as any)?.screening_invitation_path || '') as UserProfile['screeningInvitationPath'],
          hasCervix: (profile as any)?.has_cervix ?? null,
          breastScreeningRelevant: (profile as any)?.breast_screening_relevant ?? null,
          prostateScreeningRelevant: (profile as any)?.prostate_screening_relevant ?? null,
          onHrt: Boolean((profile as any)?.on_hrt),
          communityAccessStatus: ((profile as any)?.community_access_status || 'active') as UserProfile['communityAccessStatus'],
        },
        budgetLimits: (budgetRes.data || []).map(b => ({ category: b.category, limit: b.limit }) as BudgetLimit),
        budgetCategoryPreferences: (budgetCategoryRes.data || []).map((row: any) => ({
          id: row.id,
          label: row.label,
          hidden: Boolean(row.hidden),
        }) as BudgetCategoryPreference),
        expenses: (expensesRes.data || []).map((expenseRow) => mapExpenseRow(expenseRow)),
        householdShare,
        events: (eventsRes.data || []).map((e) => ({
          id: e.id,
          title: e.title,
          date: e.date,
          time: e.time,
          type: e.type,
          captureKind: (e as any).capture_kind || 'event',
          reminderStrength: (e as any).reminder_strength || 'once',
          recurrenceType: e.recurrence_type || 'one-off',
          recurrenceInterval: e.recurrence_interval,
          recurrenceUnit: e.recurrence_unit as CalendarEvent['recurrenceUnit'],
          recurrenceDayOfWeek: e.recurrence_day_of_week,
          recurrenceWeekOfMonth: e.recurrence_week_of_month,
          recurrenceMonthDay: e.recurrence_month_day,
        }) as CalendarEvent),
        chores: (choresRes.data || []).map(c => ({
          id: c.id,
          task: c.task,
          assignedTo: c.assigned_to,
          done: c.done,
          weekOf: c.week_of,
          householdShareId: c.household_share_id || null,
        })),
        lifeEvents: (lifeEventsRes.data || []).map(e => e.event) as LifeEvent[],
        benefitsViewed: (benefitsRes.data || []).map(b => b.name),
        childMaintenanceAmount: profile?.child_maintenance_amount || 0,
        childMaintenanceSchedule: profile?.child_maintenance_schedule || 'monthly',
        documentRenewals: [],
        communityDirectoryEntries: [],
        communityPolicyUpdates: [],
      };

      try {
        const savedPostcode = normalizeSavedPostcode(nextData.profile.postcode);
        const profileTags = buildCommunityPolicyProfileTags(nextData.profile);
        const [
          communityDirectoryEntriesResult,
          communityPolicyUpdatesResult,
          policyLookupResult,
        ] = await Promise.all([
          supabase
            .from('community_directory_entries')
            .select('*')
            .eq('is_active', true)
            .order('sort_order', { ascending: true }),
          supabase
            .from('community_policy_updates')
            .select('*')
            .eq('is_active', true)
            .order('sort_order', { ascending: true }),
          savedPostcode
            ? supabase.functions.invoke<CommunityPolicyLookupResult>('health-community-policy-lookup', {
                body: { postcode: savedPostcode },
              })
            : Promise.resolve({ data: null, error: null } as const),
        ]);

        if (communityDirectoryEntriesResult.error) {
          console.error(communityDirectoryEntriesResult.error);
        } else {
          nextData.communityDirectoryEntries = ((communityDirectoryEntriesResult.data || []) as any[]).map((row) => ({
            id: row.id,
            name: row.name,
            category: row.category,
            summary: row.summary,
            specialisms: row.specialisms || [],
            targetTags: row.target_tags || [],
            pricingModel: row.pricing_model,
            locationScope: row.location_scope,
            websiteUrl: row.website_url,
            phone: row.phone,
            helpfulCount: 0,
            mixedCount: 0,
            notHelpfulCount: 0,
            totalFeedback: 0,
          }));
        }

        if (communityPolicyUpdatesResult.error) {
          console.error(communityPolicyUpdatesResult.error);
        }

        const localPolicyLookup = policyLookupResult.error || (policyLookupResult.data as any)?.error
          ? null
          : ((policyLookupResult.data || null) as CommunityPolicyLookupResult | null);

        nextData.communityPolicyUpdates = [
          ...buildGeneratedCommunityPolicyUpdates({
            lookup: localPolicyLookup,
            profileTags,
          }),
          ...(((communityPolicyUpdatesResult.error ? [] : communityPolicyUpdatesResult.data) || []) as any[]).map((row) => ({
            id: row.id,
            title: row.title,
            summary: row.summary,
            sourceName: row.source_name,
            sourceUrl: row.source_url,
            reviewedAt: row.reviewed_at,
            targetTags: row.target_tags || [],
            origin: 'editorial' as const,
            localityLabel: null,
            reasonLabel: null,
            linkLabel: 'Read source',
          })),
        ];
      } catch (communityError) {
        console.error('Error loading community support data:', communityError);
      }

      setData(nextData);
      writeAppOfflineCache(userId, nextData);
      markConnectionHealthy();
    } catch (err) {
      console.error('Error fetching data:', err);
      void reportRuntimeEvent({
        userId,
        severity: isTransientConnectionError(err) ? 'warning' : 'error',
        source: 'app-data.refresh',
        message: toRuntimeMessage(err),
        path: window.location.pathname,
        metadata: {
          transient: isTransientConnectionError(err),
        },
      });
      if (isTransientConnectionError(err)) {
        const cached = readAppOfflineCache(userId);
        if (cached?.data) {
          setData({
            ...DEFAULT_DATA,
            ...cached.data,
            profile: {
              ...DEFAULT_DATA.profile,
              ...(cached.data.profile || {}),
            },
            communityDirectoryEntries: cached.data.communityDirectoryEntries || [],
            communityPolicyUpdates: cached.data.communityPolicyUpdates || [],
          });
        }
        reportConnectionIssue(err, 'Adulting could not refresh your data because the connection dropped. Your last loaded details are still on screen.');
      }
    } finally {
      setLoading(false);
    }
  }

  const refreshData = useCallback(async () => {
    if (!user) {
      return;
    }

    await Promise.all([
      fetchAllData(user.id),
      refreshSecurity(),
    ]);
  }, [refreshSecurity, user]);

  const updateProfile = useCallback(async (profile: UserProfile) => {
    if (!user) {
      throw new Error('You must be signed in to save your profile.');
    }
    const hasNamedChildren = profile.children.some((child) => child.name.trim().length > 0);
    const dependantTypes = Array.from(new Set([
      ...profile.dependantTypes.filter(Boolean),
      ...(hasNamedChildren ? (['children'] as const) : []),
    ])) as UserProfile['dependantTypes'];

    const nextProfile: UserProfile = {
      ...profile,
      dependantTypes,
    };

    setData(prev => ({ ...prev, profile: nextProfile }));

    const { data: savedProfile, error: profileError } = await supabase.from('profiles').upsert({
      user_id: user.id,
      first_name: nextProfile.firstName,
      avatar_url: nextProfile.avatarUrl || null,
      postcode: nextProfile.postcode,
      annual_income: nextProfile.annualIncome,
      employment_type: nextProfile.employmentType,
      partner_name: nextProfile.partnerName,
      partner_date_of_birth: nextProfile.partnerDateOfBirth || null,
      work_type: nextProfile.workType,
      dependant_types: nextProfile.dependantTypes,
      date_of_birth: nextProfile.dateOfBirth || null,
      screening_invitation_path: nextProfile.screeningInvitationPath,
      has_cervix: nextProfile.hasCervix,
      breast_screening_relevant: nextProfile.breastScreeningRelevant,
      prostate_screening_relevant: nextProfile.prostateScreeningRelevant,
      on_hrt: nextProfile.onHrt,
    } as any, { onConflict: 'user_id' })
      .select('*')
      .single();

    if (profileError || !savedProfile) {
      throw new Error(profileError?.message || 'Could not save your profile.');
    }

    const persistedChildIds = new Set(data.profile.children.map((child) => child.id));
    const nextChildren = nextProfile.children.filter((child) => child.name.trim().length > 0);
    const nextPersistedIds = new Set(
      nextChildren.filter((child) => persistedChildIds.has(child.id)).map((child) => child.id)
    );
    const removedChildIds = [...persistedChildIds].filter((childId) => !nextPersistedIds.has(childId));

    if (removedChildIds.length > 0) {
      await supabase.from('children').delete().in('id', removedChildIds);
    }

    const existingChildren = nextChildren.filter((child) => persistedChildIds.has(child.id));
    for (const child of existingChildren) {
      await supabase
        .from('children')
        .update({
          name: child.name,
          age: calculateAgeFromDateOfBirth(child.dateOfBirth || null) ?? child.age,
          date_of_birth: child.dateOfBirth || null,
          school_name: child.schoolName || null,
          school_urn: child.schoolUrn || null,
        })
        .eq('id', child.id)
        .eq('user_id', user.id);
    }

    const newChildren = nextChildren.filter((child) => !persistedChildIds.has(child.id));
    if (newChildren.length > 0) {
      await supabase.from('children').insert(
        newChildren.map((child) => ({
          user_id: user.id,
          name: child.name,
          age: calculateAgeFromDateOfBirth(child.dateOfBirth || null) ?? child.age,
          date_of_birth: child.dateOfBirth || null,
          school_name: child.schoolName || null,
          school_urn: child.schoolUrn || null,
        }))
      );
    }

    // Re-fetch children to get server IDs
    const { data: freshChildren } = await supabase.from('children').select('*').eq('user_id', user.id);
    if (freshChildren) {
      setData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          firstName: savedProfile.first_name || '',
          avatarUrl: (savedProfile as any)?.avatar_url || null,
          postcode: savedProfile.postcode || '',
          annualIncome: savedProfile.annual_income || 0,
          employmentType: (savedProfile.employment_type || '') as UserProfile['employmentType'],
          partnerName: (savedProfile as any)?.partner_name || '',
          partnerDateOfBirth: ((savedProfile as any)?.partner_date_of_birth || '') as string,
          workType: ((savedProfile as any)?.work_type || '') as UserProfile['workType'],
          dependantTypes,
          dateOfBirth: ((savedProfile as any)?.date_of_birth || '') as string,
          screeningInvitationPath: ((savedProfile as any)?.screening_invitation_path || '') as UserProfile['screeningInvitationPath'],
          hasCervix: (savedProfile as any)?.has_cervix ?? null,
          breastScreeningRelevant: (savedProfile as any)?.breast_screening_relevant ?? null,
          prostateScreeningRelevant: (savedProfile as any)?.prostate_screening_relevant ?? null,
          onHrt: Boolean((savedProfile as any)?.on_hrt),
          children: freshChildren.map(c => ({
            id: c.id,
            name: c.name,
            age: resolveStoredAge(c.date_of_birth || null, c.age) || 0,
            dateOfBirth: c.date_of_birth || null,
            schoolName: c.school_name || null,
            schoolUrn: c.school_urn || null,
          })),
        },
      }));
    }
    notifyDataChanged();
  }, [data.profile.children, user]);

  const updateProfileAvatar = useCallback(async (avatarUrl: string | null) => {
    if (!user) {
      throw new Error('You must be signed in to save your profile photo.');
    }

    const { data: updatedProfiles, error: updateError } = await supabase
      .from('profiles')
      .update({ avatar_url: avatarUrl || null })
      .eq('user_id', user.id)
      .select('avatar_url');

    if (updateError) {
      throw new Error(updateError.message || 'Could not save your profile photo.');
    }

    if (!updatedProfiles?.length) {
      const { data: savedProfile, error: upsertError } = await supabase
        .from('profiles')
        .upsert(
          {
            user_id: user.id,
            first_name: data.profile.firstName.trim() || null,
            avatar_url: avatarUrl || null,
            partner_date_of_birth: data.profile.partnerDateOfBirth || null,
            community_access_status: data.profile.communityAccessStatus || 'active',
          } as any,
          { onConflict: 'user_id' },
        )
        .select('avatar_url')
        .single();

      if (upsertError || !savedProfile) {
        throw new Error(upsertError?.message || 'Could not save your profile photo.');
      }
    }

    setData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        avatarUrl,
      },
    }));
    notifyDataChanged();
  }, [data.profile.communityAccessStatus, data.profile.firstName, user]);

  const updateBudgetLimits = useCallback(async (limits: BudgetLimit[]) => {
    if (!user) return;
    setData(prev => ({ ...prev, budgetLimits: limits }));
    for (const bl of limits) {
      await supabase.from('budget_limits').upsert(
        { user_id: user.id, category: bl.category, limit: bl.limit },
        { onConflict: 'user_id,category' }
      );
    }
    notifyDataChanged();
  }, [user]);

  const addBudgetCategory = useCallback(async (label: string) => {
    if (!user) return false;
    const normalizedLabel = normaliseBudgetCategoryLabel(label);
    if (!normalizedLabel) return false;

    try {
      await supabase.from('budget_category_preferences' as any).upsert(
        {
          user_id: user.id,
          label: normalizedLabel,
          hidden: false,
        },
        { onConflict: 'user_id,label' },
      );
      await fetchAllData(user.id);
      notifyDataChanged();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [user]);

  const renameBudgetCategory = useCallback(async (currentLabel: string, nextLabel: string) => {
    if (!user) return false;
    const normalizedCurrent = normaliseBudgetCategoryLabel(currentLabel);
    const normalizedNext = normaliseBudgetCategoryLabel(nextLabel);
    if (!normalizedCurrent || !normalizedNext || normalizedCurrent === normalizedNext) return false;

    try {
      const { error } = await supabase.rpc('rename_budget_category' as any, {
        current_label: normalizedCurrent,
        next_label: normalizedNext,
      });
      if (error) throw error;
      await fetchAllData(user.id);
      notifyDataChanged();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [user]);

  const setBudgetCategoryHidden = useCallback(async (label: string, hidden: boolean) => {
    if (!user) return false;
    const normalizedLabel = normaliseBudgetCategoryLabel(label);
    if (!normalizedLabel) return false;

    try {
      await supabase.from('budget_category_preferences' as any).upsert(
        {
          user_id: user.id,
          label: normalizedLabel,
          hidden,
        },
        { onConflict: 'user_id,label' },
      );
      await fetchAllData(user.id);
      notifyDataChanged();
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }, [user]);

  const updateChildMaintenance = useCallback(async (amount: number, schedule: string) => {
    if (!user) return;
    setData((prev) => ({
      ...prev,
      childMaintenanceAmount: amount,
      childMaintenanceSchedule: schedule,
    }));
    await supabase.from('profiles').update({
      child_maintenance_amount: amount,
      child_maintenance_schedule: schedule,
    } as any).eq('user_id', user.id);
    notifyDataChanged();
  }, [user]);

  const addExpense = useCallback(async (expense: Expense, receiptFile?: File | null) => {
    if (!user) return false;
    const activeHouseholdShareId = data.householdShare?.id || null;
    const isHouseholdExpense = expense.visibility === 'household' && Boolean(activeHouseholdShareId);
    const queuedExpense = {
      ...expense,
      householdShareId: isHouseholdExpense ? activeHouseholdShareId : null,
    };

    let uploadedReceipt:
      | {
          receiptFileName: string;
          receiptFileType: string;
          receiptFileSize: number;
          receiptStoragePath: string;
        }
      | null = null;

    try {
      if (receiptFile) {
        const receiptStoragePath = buildExpenseReceiptStoragePath(user.id, expense.id, receiptFile.name);
        const { error: uploadError } = await supabase.storage.from('user-documents').upload(receiptStoragePath, receiptFile);
        if (uploadError) {
          throw uploadError;
        }

        uploadedReceipt = {
          receiptFileName: receiptFile.name,
          receiptFileType: receiptFile.type || 'application/octet-stream',
          receiptFileSize: receiptFile.size,
          receiptStoragePath,
        };
      }

      const { data: inserted, error } = await supabase.from('expenses').insert({
        user_id: user.id,
        amount: expense.amount,
        category: expense.category,
        date: expense.date,
        description: expense.description,
        expense_scope: 'personal',
        household_share_id: isHouseholdExpense ? activeHouseholdShareId : null,
        is_recurring: Boolean(expense.isRecurring),
        recurring_period: expense.recurringPeriod || 'none',
        is_subscription: Boolean(expense.isSubscription && expense.isRecurring),
        cancel_intent: Boolean(expense.cancelIntent && expense.isSubscription && expense.isRecurring),
        next_due_date: expense.isRecurring ? (expense.nextDueDate || expense.date) : null,
        reminder_days_before: expense.isRecurring ? (expense.reminderDaysBefore ?? null) : null,
        last_paid_at: expense.lastPaidAt || null,
        last_paid_amount: expense.lastPaidAmount ?? null,
        notes: expense.notes?.trim() || null,
        receipt_file_name: uploadedReceipt?.receiptFileName || null,
        receipt_file_type: uploadedReceipt?.receiptFileType || null,
        receipt_file_size: uploadedReceipt?.receiptFileSize ?? null,
        receipt_storage_path: uploadedReceipt?.receiptStoragePath || null,
      }).select().single();

      if (error) {
        throw error;
      }

      if (inserted) {
        setData((prev) => ({
          ...prev,
          expenses: [...prev.expenses, mapExpenseRow(inserted as any)],
        }));
      }
      notifyDataChanged();
      return true;
    } catch (error) {
      console.error('Error saving expense:', error);
      if (uploadedReceipt?.receiptStoragePath) {
        await supabase.storage.from('user-documents').remove([uploadedReceipt.receiptStoragePath]).catch(() => undefined);
      }

      if ((isOffline || isTransientConnectionError(error)) && !receiptFile) {
        const nextData: AppData = {
          ...data,
          expenses: [...data.expenses, queuedExpense],
        };
        setData(nextData);
        writeAppOfflineCache(user.id, nextData);
        writeQueuedCoreWrites(user.id, [
          ...readQueuedCoreWrites(user.id),
          {
            id: crypto.randomUUID(),
            kind: 'add-expense',
            createdAt: new Date().toISOString(),
            expense: {
              id: queuedExpense.id,
              amount: queuedExpense.amount,
              category: queuedExpense.category,
              date: queuedExpense.date,
              description: queuedExpense.description,
              visibility: queuedExpense.visibility,
              householdShareId: queuedExpense.householdShareId,
              isRecurring: queuedExpense.isRecurring,
              recurringPeriod: queuedExpense.recurringPeriod,
              isSubscription: queuedExpense.isSubscription,
              cancelIntent: queuedExpense.cancelIntent,
              nextDueDate: queuedExpense.nextDueDate,
              reminderDaysBefore: queuedExpense.reminderDaysBefore,
              lastPaidAt: queuedExpense.lastPaidAt,
              lastPaidAmount: queuedExpense.lastPaidAmount,
              notes: queuedExpense.notes || null,
            },
          },
        ]);
        reportConnectionIssue(
          error,
          'Adulting saved that expense on this device and will sync it when the connection settles.',
        );
        notifyDataChanged();
        return true;
      }

      if ((isOffline || isTransientConnectionError(error)) && receiptFile) {
        reportConnectionIssue(
          error,
          'Adulting needs a live connection to upload the receipt. Save the expense without the receipt for now, or try again when the connection settles.',
        );
      }
      return false;
    }
  }, [data, isOffline, reportConnectionIssue, user]);

  const updateRecurringExpenseFlags = useCallback(async (
    expenseId: string,
    updates: { isSubscription?: boolean; cancelIntent?: boolean },
  ) => {
    if (!user) return false;

    const sourceExpense = data.expenses.find((expense) => expense.id === expenseId);
    if (!sourceExpense?.isRecurring || !sourceExpense.recurringPeriod || sourceExpense.recurringPeriod === 'none') {
      return false;
    }

    const nextIsSubscription =
      updates.isSubscription ?? sourceExpense.isSubscription ?? Boolean(updates.cancelIntent);
    const nextCancelIntent = nextIsSubscription
      ? (updates.cancelIntent ?? sourceExpense.cancelIntent ?? false)
      : false;

    const { data: updated, error } = await supabase
      .from('expenses')
      .update({
        is_subscription: nextIsSubscription,
        cancel_intent: nextCancelIntent,
      } as any)
      .eq('id', expenseId)
      .select()
      .single();

    if (error) {
      console.error(error);
      return false;
    }

    if (updated) {
      const nextExpense = mapExpenseRow(updated as any);
      setData((prev) => ({
        ...prev,
        expenses: prev.expenses.map((expense) => (expense.id === expenseId ? nextExpense : expense)),
      }));
    }
    notifyDataChanged();
    return true;
  }, [data.expenses, user]);

  const recordRecurringExpensePayment = useCallback(async (expenseId: string, amount: number) => {
    if (!user) return;

    const sourceExpense = data.expenses.find((expense) => expense.id === expenseId);
    if (!sourceExpense?.isRecurring || !sourceExpense.recurringPeriod || sourceExpense.recurringPeriod === 'none') {
      return;
    }

    const dueDate = sourceExpense.nextDueDate || getNextRecurringExpenseDate(sourceExpense.date, sourceExpense.recurringPeriod);
    if (!dueDate) {
      return;
    }

    const paidDate = new Date().toISOString().slice(0, 10);
    const nextDueDate = getNextRecurringExpenseDate(
      dueDate,
      sourceExpense.recurringPeriod,
      new Date(`${dueDate}T12:00:00`),
    );

    const [updatedTemplateResult, insertedPaymentResult] = await Promise.all([
      supabase
        .from('expenses')
        .update({
          next_due_date: nextDueDate,
          last_paid_at: paidDate,
          last_paid_amount: amount,
        } as any)
        .eq('id', expenseId)
        .select()
        .single(),
      supabase
        .from('expenses')
        .insert({
          user_id: user.id,
          amount,
          category: sourceExpense.category,
          date: paidDate,
          description: sourceExpense.description,
          expense_scope: 'personal',
          household_share_id: sourceExpense.householdShareId || null,
          is_recurring: false,
          recurring_period: 'none',
          is_subscription: false,
          cancel_intent: false,
          notes: `Logged from payment tracker for ${dueDate}.`,
        } as any)
        .select()
        .single(),
    ]);

    if (updatedTemplateResult.error || insertedPaymentResult.error) {
      console.error(updatedTemplateResult.error || insertedPaymentResult.error);
      await fetchAllData(user.id);
      throw updatedTemplateResult.error || insertedPaymentResult.error;
    }

    const updatedTemplate = updatedTemplateResult.data ? mapExpenseRow(updatedTemplateResult.data as any) : null;
    const insertedPayment = insertedPaymentResult.data ? mapExpenseRow(insertedPaymentResult.data as any) : null;

    setData((prev) => ({
      ...prev,
      expenses: prev.expenses.flatMap((expense) => {
        if (expense.id !== expenseId) return [expense];
        return [updatedTemplate || expense];
      }).concat(insertedPayment ? [insertedPayment] : []),
    }));
    notifyDataChanged();
  }, [data.expenses, user]);

  const bulkAddExpenses = useCallback(async (expenses: Expense[]) => {
    if (!user || expenses.length === 0) return 0;

    const existingFingerprints = new Set(data.expenses.map((expense) => expenseFingerprint(expense)));
    const batchFingerprints = new Set<string>();

    const freshExpenses = expenses.filter((expense) => {
      const fingerprint = expenseFingerprint(expense);
      if (existingFingerprints.has(fingerprint) || batchFingerprints.has(fingerprint)) {
        return false;
      }
      batchFingerprints.add(fingerprint);
      return true;
    });

    if (freshExpenses.length === 0) return 0;

    const { data: insertedRows, error } = await supabase
      .from('expenses')
      .insert(
        freshExpenses.map((expense) => ({
          user_id: user.id,
          amount: expense.amount,
          category: expense.category,
          date: expense.date,
          description: expense.description,
          expense_scope: 'personal',
          household_share_id: null,
          is_recurring: false,
          recurring_period: 'none',
        }))
      )
      .select();

    if (error) {
      console.error('Error syncing expenses:', error);
      return 0;
    }

    const insertedExpenses = (insertedRows || []).map((row) => ({
      ...mapExpenseRow(row),
    })) as Expense[];

    setData((previous) => ({
      ...previous,
      expenses: [...previous.expenses, ...insertedExpenses],
    }));
    notifyDataChanged();
    return insertedExpenses.length;
  }, [data.expenses, user]);

  const deleteExpense = useCallback(async (id: string) => {
    if (!user) return;
    const existingExpense = data.expenses.find((expense) => expense.id === id);
    setData(prev => ({ ...prev, expenses: prev.expenses.filter(e => e.id !== id) }));
    if (existingExpense?.receiptStoragePath) {
      await supabase.storage.from('user-documents').remove([existingExpense.receiptStoragePath]).catch(() => undefined);
    }
    await supabase.from('expenses').delete().eq('id', id);
    notifyDataChanged();
  }, [data.expenses, user]);

  const addEvent = useCallback(async (event: CalendarEvent) => {
    if (!user) return null;
    setData(prev => ({ ...prev, events: [...prev.events, event] }));
    const { data: inserted } = await supabase.from('events').insert({
      user_id: user.id,
      title: event.title,
      date: event.date,
      time: event.time,
      type: event.type,
      capture_kind: event.captureKind || 'event',
      reminder_strength: event.reminderStrength || 'once',
      recurrence_type: event.recurrenceType || 'one-off',
      recurrence_interval: event.recurrenceInterval ?? null,
      recurrence_unit: event.recurrenceUnit ?? null,
      recurrence_day_of_week: event.recurrenceDayOfWeek ?? null,
      recurrence_week_of_month: event.recurrenceWeekOfMonth ?? null,
      recurrence_month_day: event.recurrenceMonthDay ?? null,
    }).select().single();
    if (inserted) {
      const savedEvent = {
        ...event,
        id: inserted.id,
        captureKind: (inserted as any).capture_kind || event.captureKind || 'event',
        reminderStrength: (inserted as any).reminder_strength || event.reminderStrength || 'once',
        recurrenceType: inserted.recurrence_type || event.recurrenceType || 'one-off',
        recurrenceInterval: inserted.recurrence_interval,
        recurrenceUnit: inserted.recurrence_unit as CalendarEvent['recurrenceUnit'],
        recurrenceDayOfWeek: inserted.recurrence_day_of_week,
        recurrenceWeekOfMonth: inserted.recurrence_week_of_month,
        recurrenceMonthDay: inserted.recurrence_month_day,
      } satisfies CalendarEvent;

      setData(prev => ({
        ...prev,
        events: prev.events.map((e) => e.id === event.id ? savedEvent : e),
      }));
      notifyDataChanged();
      return savedEvent;
    }
    notifyDataChanged();
    return event;
  }, [user]);

  const deleteEvent = useCallback(async (id: string) => {
    if (!user) return;
    setData(prev => ({ ...prev, events: prev.events.filter(e => e.id !== id) }));
    await supabase.from('events').delete().eq('id', id);
    notifyDataChanged();
  }, [user]);

  const addChore = useCallback(async (chore: Chore) => {
    if (!user) return;
    const queuedChore = {
      ...chore,
      householdShareId: data.householdShare?.id || null,
    };

    const optimisticData: AppData = {
      ...data,
      chores: [...data.chores, queuedChore],
    };
    setData(optimisticData);
    writeAppOfflineCache(user.id, optimisticData);

    try {
      const { data: inserted, error } = await supabase
        .from('chores')
        .insert({
          user_id: user.id,
          task: chore.task,
          assigned_to: chore.assignedTo,
          done: chore.done,
          week_of: chore.weekOf,
          household_share_id: data.householdShare?.id || null,
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (inserted) {
        const syncedData: AppData = {
          ...optimisticData,
          chores: optimisticData.chores.map((existing) =>
            existing.id === chore.id
              ? { ...existing, id: inserted.id, householdShareId: inserted.household_share_id || null }
              : existing,
          ),
        };
        setData(syncedData);
        writeAppOfflineCache(user.id, syncedData);
      }
      markConnectionHealthy();
      notifyDataChanged();
    } catch (error) {
      if (isOffline || isTransientConnectionError(error)) {
        writeQueuedCoreWrites(user.id, [
          ...readQueuedCoreWrites(user.id),
          {
            id: crypto.randomUUID(),
            kind: 'add-chore',
            createdAt: new Date().toISOString(),
            chore: {
              id: queuedChore.id,
              task: queuedChore.task,
              assignedTo: queuedChore.assignedTo,
              done: queuedChore.done,
              weekOf: queuedChore.weekOf,
              householdShareId: queuedChore.householdShareId,
            },
          },
        ]);
        reportConnectionIssue(
          error,
          'Adulting saved that checklist item on this device and will sync it when the connection settles.',
        );
        notifyDataChanged();
        return;
      }

      setData(data);
      writeAppOfflineCache(user.id, data);
      console.error('Error saving chore:', error);
      void reportRuntimeEvent({
        userId: user.id,
        severity: 'error',
        source: 'app-data.add-chore',
        message: toRuntimeMessage(error),
        path: window.location.pathname,
      });
      return;
    }
  }, [data, isOffline, markConnectionHealthy, reportConnectionIssue, user]);

  const toggleChore = useCallback(async (id: string) => {
    if (!user) return;
    const chore = data.chores.find(c => c.id === id);
    if (!chore) return;
    const newDone = !chore.done;
    const nextData: AppData = {
      ...data,
      chores: data.chores.map((existing) => (existing.id === id ? { ...existing, done: newDone } : existing)),
    };
    setData(nextData);
    writeAppOfflineCache(user.id, nextData);

    try {
      const { error } = await supabase.from('chores').update({ done: newDone }).eq('id', id);
      if (error) {
        throw error;
      }
      markConnectionHealthy();
      notifyDataChanged();
    } catch (error) {
      if (isOffline || isTransientConnectionError(error)) {
        writeQueuedCoreWrites(user.id, [
          ...readQueuedCoreWrites(user.id),
          {
            id: crypto.randomUUID(),
            kind: 'toggle-chore',
            createdAt: new Date().toISOString(),
            choreId: id,
            done: newDone,
          },
        ]);
        reportConnectionIssue(
          error,
          'Adulting saved that checklist update on this device and will sync it when the connection settles.',
        );
        notifyDataChanged();
        return;
      }

      setData(data);
      writeAppOfflineCache(user.id, data);
      console.error('Error updating chore:', error);
      void reportRuntimeEvent({
        userId: user.id,
        severity: 'error',
        source: 'app-data.toggle-chore',
        message: toRuntimeMessage(error),
        path: window.location.pathname,
      });
      return;
    }
  }, [data, isOffline, markConnectionHealthy, reportConnectionIssue, user]);

  const addLifeEvent = useCallback(async (event: LifeEvent) => {
    if (!user) return;
    if (data.lifeEvents.includes(event)) return;
    setData(prev => ({ ...prev, lifeEvents: [...prev.lifeEvents, event] }));
    await supabase.from('life_events').insert({ user_id: user.id, event });
    notifyDataChanged();
  }, [user, data.lifeEvents]);

  const markBenefitViewed = useCallback(async (name: string) => {
    if (!user) return;
    if (data.benefitsViewed.includes(name)) return;
    setData(prev => ({ ...prev, benefitsViewed: [...prev.benefitsViewed, name] }));
    try { await supabase.from('benefits_viewed').insert({ user_id: user.id, name }); } catch {}
    notifyDataChanged();
  }, [user, data.benefitsViewed]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    if (user?.id) {
      clearAppOfflineCache(user.id);
      removeScopedLocalJson('calendar-items', user.id);
      removeScopedLocalJson('documents', user.id);
      writeQueuedCoreWrites(user.id, []);
    }
    setData(DEFAULT_DATA);
    setRole('user');
    setRequiresMfa(false);
    setHasVerifiedMfaFactor(false);
    setRequiresPrivilegedMfaSetup(false);
    setMfaLevel(null);
    setSecurityLoading(false);
  }, [user?.id]);

  const ctx: AppContextType = {
    data,
    user,
    loading,
    securityLoading,
    role,
    isPrivilegedUser: role === 'admin' || role === 'moderator',
    mfaLevel,
    hasVerifiedMfaFactor,
    requiresMfa,
    requiresPrivilegedMfaSetup,
    refreshSecurity,
    refreshData,
    updateProfile, updateProfileAvatar, updateChildMaintenance, updateBudgetLimits, addBudgetCategory, renameBudgetCategory, setBudgetCategoryHidden, addExpense, updateRecurringExpenseFlags, recordRecurringExpensePayment, bulkAddExpenses, deleteExpense,
    addEvent, deleteEvent, addChore, toggleChore, addLifeEvent, markBenefitViewed, signOut,
  };

  return <AppContext.Provider value={ctx}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
